import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ModuleProvider } from "./core/ModuleProvider";
import { ProtectedRoute } from "./components/Auth/ProtectedRoute";
import { Logo } from "./components/Logo/Logo";
import { Button } from "./components/Button/Button";
import { contact_info } from "./constants/contact";

// Import pages
import Dashboard from "./pages/Dashboard";
import Callback from "./pages/Callback";
import Signup from "./pages/Signup";
import Verify from "./pages/Verify";
import ResetPassword from "./pages/ResetPassword";
import CliSuccess from "./pages/CliSuccess";
import "./pages/Callback.css";

// Import modules
import { HeroModule } from "./modules/hero/HeroModule";
import { ContactModule } from "./modules/contact/ContactModule";
import { FooterModule } from "./modules/footer/FooterModule";

import "./App.css";

// Navigation component with auth awareness
const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { login, isAuthenticated, user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handle_request_hosting = (): void => {
    const contact_section = document.querySelector<HTMLElement>("#contact");
    contact_section?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      className={`nav ${isScrolled ? "nav--scrolled" : ""} ${
        isAuthenticated ? "nav--authenticated" : ""
      }`}
    >
      {isAuthenticated ? (
        <>
          <div className="nav__container nav__container--primary">
            <div className="nav__brand">
              <Logo variant="light" size="small" />
            </div>
            <div className="nav__actions nav__actions--primary">
              <span className="nav__user-email">{user?.profile?.email}</span>
              <a href="/dashboard" className="nav__link nav__link--auth">
                Dashboard
              </a>
            </div>
          </div>
          <div className="nav__container nav__container--secondary">
            <div className="nav__menu">
              <a href="#hosting" className="nav__link">
                Hosting
              </a>
              <a href="#contact" className="nav__link">
                Contact
              </a>
            </div>
            <div className="nav__actions nav__actions--secondary">
              <a className="nav__contact" href={`tel:${contact_info.phone_link}`}>
                {contact_info.phone_display}
              </a>
              <Button
                variant="primary"
                size="small"
                onClick={handle_request_hosting}
              >
                Request Hosting
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="nav__container">
          <div className="nav__brand">
            <Logo variant="light" size="small" />
          </div>

          <div className="nav__menu">
            <a href="#hosting" className="nav__link">
              Hosting
            </a>
            <a href="#contact" className="nav__link">
              Contact
            </a>
          </div>

          <div className="nav__actions">
            <button onClick={login} className="nav__link nav__link--auth">
              Login
            </button>
            <a href="/signup" className="nav__link nav__link--auth">
              Sign Up
            </a>
            <a className="nav__contact" href={`tel:${contact_info.phone_link}`}>
              {contact_info.phone_display}
            </a>
            <Button
              variant="primary"
              size="small"
              onClick={handle_request_hosting}
            >
              Request Hosting
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Main landing page
const LandingPage: React.FC = () => {
  const handle_request_hosting = (): void => {
    const contact_section = document.querySelector<HTMLElement>("#contact");
    contact_section?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="app">
      <Navigation />

      <main className="main">
        <HeroModule on_request_hosting={handle_request_hosting} />
        <ContactModule />
      </main>

      <FooterModule />
    </div>
  );
};

// App component with routing
function App() {
  return (
    <Router>
      <AuthProvider>
        <ModuleProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/callback" element={<Callback />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/cli-success" element={<CliSuccess />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/terms"
              element={<div className="page-placeholder">Terms of Service</div>}
            />
            <Route
              path="/privacy"
              element={<div className="page-placeholder">Privacy Policy</div>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ModuleProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
